import React, { useState, useRef, useCallback, forwardRef } from "react";
import { 
  Box, 
  ToggleButton, 
  ToggleButtonGroup, 
  Typography, 
  Paper,
  Grid,
  TextField,
} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import Button from "@mui/material/Button";
import { ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests";
import { IPDCAttachFileWithReset, IPDCToastMessageResult } from "../../../../components/Controls";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import ViewMap from "../../LandInfraDevelopment/GISView/MapRelated/Views/ViewMap";
import { fromLonLat } from "ol/proj";

// Global map data for highlighting
let mapData = {
  id: "",
  type: "",
  operationType: "",
  rowData: { upin_1: "" },
  spatialData: "",
  originalParcels: "",
  newParcels: "",
  parkeId: "",
};

// Reserve Parcel Form
const ReserveParcelForm = forwardRef(({ selectedFeatureOnMap, onClose }, ref) => {
  const [upin, setUpin] = useState("");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef();

  const clearMessage = useCallback(() => {
    const timer = setTimeout(() => {
      setResponseMessage("");
      setAlertSeverity("info");
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const showToastMessage = useCallback((message, severity) => {
    setResponseMessage("");
    setTimeout(() => {
      setResponseMessage(message);
      setAlertSeverity(severity);
      clearMessage();
    }, 10);
  }, [clearMessage]);

  React.useEffect(() => {
    if (selectedFeatureOnMap && selectedFeatureOnMap.length > 0) {
      const feature = selectedFeatureOnMap[0];
      const upinFromMap = feature.get("upin") || "";
      const parcelStatus = feature.get("status") || "";
      const occupancyStatus = feature.get("occupancy_status") || "";
      
      if (upinFromMap) {
        if (occupancyStatus === "Occupied" || parcelStatus === "reserved") {
          showToastMessage("This parcel cannot be reserved. It is either occupied or already reserved.", "warning");
          setUpin("");
        } else {
          setUpin(upinFromMap);
        }
      }
    }
  }, [selectedFeatureOnMap]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      showToastMessage("Please wait, previous request is processing...", "info");
      return;
    }
    
    setIsSubmitting(true);

    try {
      if (!upin.trim()) {
        showToastMessage("Please select an available parcel by clicking on the map", "warning");
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append("upin", upin);
      formData.append("description", description);
      if (attachment) {
        formData.append("file", attachment);
      }

      const responseData = await ExecutePostWithParams(`parcels/${upin}/placeOnHold`, formData, true);

      if (responseData) {
        showToastMessage("Parcel placed on hold successfully!", "success");
        resetForm();
        if (onClose) onClose();
      } else {
        showToastMessage("Reservation failed!", "error");
      }
    } catch (error) {
      console.error(error);
      showToastMessage(`Reservation failed: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setUpin("");
    setDescription("");
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.reset();
    }
  };

  return (
    <Paper sx={{ 
      width: 380, 
      mt: 2,
      p: 2,
      borderRadius: 2,
      boxShadow: 3,
      maxHeight: 'calc(100vh - 120px)',
      overflow: 'auto'
    }} elevation={3}>
      <Typography variant="h6" gutterBottom align="center">
        Reserve Parcel
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: "center", fontSize: '0.75rem' }}>
        Select available parcels (not occupied/orange colored)
      </Typography>
      <form onSubmit={handleSubmit} ref={ref}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField 
              fullWidth 
              label="Parcel UPIN" 
              size="small"
              variant="outlined" 
              value={upin} 
              onChange={e => setUpin(e.target.value)} 
              required 
              helperText="Click on an available parcel in the map"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField 
              fullWidth 
              label="Description" 
              variant="outlined" 
              multiline 
              rows={3} 
              size="small"
              value={description} 
              onChange={e => setDescription(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12}>
            <IPDCAttachFileWithReset 
              ref={fileInputRef}
              onChange={files => setAttachment(files[0])} 
              isMultiple={true} 
              isRequired={false}
              label="Attach Document"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography color={"red"} variant="caption" align="right" display="block">
              Note: This reservation will expire in 30 days
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth
              disabled={isSubmitting}
              size="small"
            >
              {isSubmitting ? "Reserving..." : "Reserve Parcel"}
            </Button>
          </Grid>
        </Grid>
      </form>
      
      {responseMessage && (
        <IPDCToastMessageResult 
          message={responseMessage} 
          type={alertSeverity} 
          onClose={() => setResponseMessage("")}
        />
      )}
    </Paper>
  );
});

// Release Parcel Form
const ReleaseParcelForm = forwardRef(({ selectedFeatureOnMap, onClose }, ref) => {
  const [upin, setUpin] = useState("");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef();

  const clearMessage = useCallback(() => {
    const timer = setTimeout(() => {
      setResponseMessage("");
      setAlertSeverity("info");
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const showToastMessage = useCallback((message, severity) => {
    setResponseMessage("");
    setTimeout(() => {
      setResponseMessage(message);
      setAlertSeverity(severity);
      clearMessage();
    }, 10);
  }, [clearMessage]);

  React.useEffect(() => {
    if (selectedFeatureOnMap && selectedFeatureOnMap.length > 0) {
      const feature = selectedFeatureOnMap[0];
      const upinFromMap = feature.get("upin") || "";
      const parcelStatus = feature.get("occupancy_status") || "";
      
      if (upinFromMap && parcelStatus === "Reserved") {
        setUpin(upinFromMap);
      }
    }
  }, [selectedFeatureOnMap]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      showToastMessage("Please wait, previous request is processing...", "info");
      return;
    }
    
    setIsSubmitting(true);

    try {
      if (!upin.trim()) {
        showToastMessage("Please select a reserved parcel by clicking on the map", "warning");
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append("upin", upin);
      if (description) formData.append("description", description);
      if (attachment) formData.append("file", attachment);

      const responseData = await ExecutePostWithParams(`parcels/${upin}/placeOnHoldRelease`, formData, true);

      if (responseData) {
        showToastMessage("Parcel released successfully!", "success");
        resetForm();
        if (onClose) onClose();
      } else {
        showToastMessage("Release failed!", "error");
      }
    } catch (error) {
      console.error(error);
      showToastMessage(`Release failed: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setUpin("");
    setDescription("");
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.reset();
  };

  return (
    <Paper sx={{ 
      width: 380, 
      mt: 2,
      p: 2,
      borderRadius: 2,
      boxShadow: 3,
      maxHeight: 'calc(100vh - 120px)',
      overflow: 'auto'
    }} elevation={3}>
      <Typography variant="h6" gutterBottom align="center">
        Release Parcel
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: "center", fontSize: '0.75rem' }}>
        Select reserved parcels only
      </Typography>
      <form onSubmit={handleSubmit} ref={ref}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField 
              fullWidth 
              label="Parcel UPIN" 
              size="small"
              variant="outlined" 
              value={upin} 
              onChange={e => setUpin(e.target.value)} 
              required 
              helperText="Click on a reserved parcel in the map"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField 
              fullWidth 
              label="Description" 
              variant="outlined" 
              multiline 
              rows={3} 
              size="small"
              value={description} 
              onChange={e => setDescription(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12}>
            <IPDCAttachFileWithReset 
              ref={fileInputRef}
              onChange={files => setAttachment(files[0])} 
              isMultiple={true} 
              isRequired={false}
              label="Attach Document"
            />
          </Grid>
          <Grid item xs={12}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth
              disabled={isSubmitting}
              size="small"
            >
              {isSubmitting ? "Releasing..." : "Release Parcel"}
            </Button>
          </Grid>
        </Grid>
      </form>
      
      {responseMessage && (
        <IPDCToastMessageResult 
          message={responseMessage} 
          type={alertSeverity} 
          onClose={() => setResponseMessage("")}
        />
      )}
    </Paper>
  );
});

// Propose Parcel Form Component
const ProposeParcelForm = forwardRef(({ selectedFeatureOnMap, onFetchCenter, onClose }, ref) => {
  const [upin, setUpin] = useState("");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [parcelInPark, setParcelInPark] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef();

  const clearMessage = useCallback(() => {
    const timer = setTimeout(() => {
      setResponseMessage("");
      setAlertSeverity("info");
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const showToastMessage = useCallback((message, severity) => {
    setResponseMessage("");
    setTimeout(() => {
      setResponseMessage(message);
      setAlertSeverity(severity);
      clearMessage();
    }, 10);
  }, [clearMessage]);

  React.useEffect(() => {
    if (selectedFeatureOnMap && selectedFeatureOnMap.length > 0) {
      const feature = selectedFeatureOnMap[0];
      const upinFromMap = feature.get("upin") || "";
      if (upinFromMap) {
        setUpin(upinFromMap);
        showToastMessage(`Parcel ${upinFromMap} selected for proposal`, "success");
      }
    }
  }, [selectedFeatureOnMap]);

  React.useEffect(() => {
    const fetchParcels = async () => {
      try {
        const responseData = await GetDataFromApiWithParams(`/getReserveRequest`);
        if (responseData?.success && Array.isArray(responseData.data)) {
          setParcelInPark(responseData.data);
        }
      } catch (error) {
        console.error("Error fetching parcels:", error);
      }
    };
    fetchParcels();
  }, []);

  const handleViewMap = async () => {
    mapData.rowData.upin_1 = upin;
    if (!upin) {
      showToastMessage("Please select a parcel from dropdown first", "warning");
      return;
    }
    
    if (onFetchCenter) {
      const center = await onFetchCenter(upin);
      if (!center) {
        showToastMessage("Could not find park center for this parcel", "warning");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      showToastMessage("Please wait, previous request is processing...", "info");
      return;
    }
    
    setIsSubmitting(true);

    try {
      if (!upin.trim()) {
        showToastMessage("Please select a parcel by clicking dropdown", "warning");
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append("upin", upin);
      formData.append("description", description);
      if (attachment) formData.append("file", attachment);

      const responseData = await ExecutePostWithParams(`parcels/${upin}/propose`, formData, true);

      if (responseData) {
        showToastMessage("Proposed parcel is successfully reserved!", "success");
        resetForm();
        if (onClose) onClose();
      } else {
        showToastMessage("Proposal failed!", "error");
      }
    } catch (error) {
      console.error(error);
      showToastMessage(`Proposal failed: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setUpin("");
    setDescription("");
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.reset();
  };

  return (
    <Paper sx={{ 
      width: 380, 
      mt: 2,
      p: 2,
      borderRadius: 2,
      boxShadow: 3,
      maxHeight: 'calc(100vh - 120px)',
      overflow: 'auto'
    }} elevation={3}>
      <Typography variant="h6" gutterBottom align="center">
        Proposed Parcels to Reserve
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: "center", fontSize: '0.75rem' }}>
        Select parcels to approve
      </Typography>
      <form onSubmit={handleSubmit} ref={ref}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container spacing={1} alignItems="flex-end">
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <InputLabel id="upin_no-label">Parcel UPIN</InputLabel>
                  <Select
                    labelId="upin_no-label"
                    value={upin}
                    onChange={e => setUpin(e.target.value)}
                    label="Parcel UPIN"
                  >
                    <MenuItem value=""><em>Select Parcel UPIN</em></MenuItem>
                    {parcelInPark?.length > 0 ? (
                      parcelInPark.map(parcel => (
                        <MenuItem key={parcel?.upin} value={parcel?.upin}>
                          {parcel?.upin}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled><em>No available parcel</em></MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  size="small"
                  startIcon={<MapIcon fontSize="small" />}
                  onClick={handleViewMap}
                  fullWidth
                >
                  View Map
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <TextField 
              fullWidth 
              label="Description" 
              multiline 
              rows={3} 
              size="small"
              value={description} 
              onChange={e => setDescription(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12}>
            <IPDCAttachFileWithReset 
              ref={fileInputRef}
              onChange={files => setAttachment(files[0])} 
              isMultiple={true} 
              isRequired={false}
              label="Attach Document"
            />
          </Grid>
          <Grid item xs={12}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth
              disabled={isSubmitting}
              size="small"
            >
              {isSubmitting ? "Proposing..." : "Approve Parcel Reserve"}
            </Button>
          </Grid>
        </Grid>
      </form>
      
      {responseMessage && (
        <IPDCToastMessageResult 
          message={responseMessage} 
          type={alertSeverity} 
          onClose={() => setResponseMessage("")}
        />
      )}
    </Paper>
  );
});

// Main Component
const ParcelReservationRelease = () => {
  const [selectedFeatureOnMap, setSelectedFeatureOnMap] = useState(null);
  const [showReserveForm, setShowReserveForm] = useState(false);
  const [showReleaseForm, setShowReleaseForm] = useState(false);
  const [showProposeForm, setShowProposeForm] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [defaultCenter, setDefaultCenter] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [mapReady, setMapReady] = useState(false);

  // Fetch default park center on mount
  React.useEffect(() => {
    const fetchDefaultPark = async () => {
      try {
        const parksResponse = await GetDataFromApiWithParams("/parks");
        if (parksResponse && parksResponse.length > 0) {
          const firstPark = parksResponse[0];
          if (firstPark.center_of_park?.coordinates) {
            const center = fromLonLat(firstPark.center_of_park.coordinates);
            setDefaultCenter(center);
          } else {
            const fallbackCenter = fromLonLat([38.7578, 8.9806]);
            setDefaultCenter(fallbackCenter);
          }
        } else {
          const fallbackCenter = fromLonLat([38.7578, 8.9806]);
          setDefaultCenter(fallbackCenter);
        }
      } catch (error) {
        console.error("Error fetching default park:", error);
        const fallbackCenter = fromLonLat([38.7578, 8.9806]);
        setDefaultCenter(fallbackCenter);
      }
    };
    
    fetchDefaultPark();
  }, []);

  // Initialize map after component mounts
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setMapReady(true);
      setMapKey(prev => prev + 1);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const fetchParkCenter = async (upin) => {
    try {
      const parcelResponse = await GetDataFromApiWithParams(`/parcels/${upin}`);
      if (parcelResponse && parcelResponse.length > 0 && parcelResponse[0].park_id) {
        const parkResponse = await GetDataFromApiWithParams(`/parks/${parcelResponse[0].park_id}`);
        if (parkResponse && parkResponse.length > 0 && parkResponse[0].center_of_park) {
          const centerCoords = parkResponse[0].center_of_park.coordinates;
          const newCenter = fromLonLat(centerCoords);
          setMapCenter(newCenter);
          return newCenter;
        }
      }
      return null;
    } catch (error) {
      console.error("Error fetching park center:", error);
      return null;
    }
  };

  const closeAllForms = () => {
    setShowReserveForm(false);
    setShowReleaseForm(false);
    setShowProposeForm(false);
  };

  const handleReserveButton = () => {
    setShowReserveForm(!showReserveForm);
    setShowReleaseForm(false);
    setShowProposeForm(false);
  };

  const handleReleaseButton = () => {
    setShowReleaseForm(!showReleaseForm);
    setShowReserveForm(false);
    setShowProposeForm(false);
  };

  const handleProposeButton = () => {
    setShowProposeForm(!showProposeForm);
    setShowReserveForm(false);
    setShowReleaseForm(false);
  };

  const finalCenter = mapCenter || defaultCenter || fromLonLat([38.7578, 8.9806]);

  return (
    <Box sx={{ height: '100vh', width: '100%', position: 'relative' }}>
      {/* Toggle Buttons Container - Floated on top right */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end'
      }}>
        {/* Toggle Buttons */}
        <Box sx={{ 
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 2,
          p: 0.5
        }}>
          <ToggleButtonGroup exclusive size="small">
            <ToggleButton 
              value="Reserve" 
              onClick={handleReserveButton} 
              selected={showReserveForm}
              sx={{ px: 2 }}
            >
              <Typography variant="body2" color={showReserveForm ? "primary" : "gray"}>
                Reserve
              </Typography>
            </ToggleButton>
            <ToggleButton 
              value="Release" 
              onClick={handleReleaseButton} 
              selected={showReleaseForm}
              sx={{ px: 2 }}
            >
              <Typography variant="body2" color={showReleaseForm ? "primary" : "gray"}>
                Release
              </Typography>
            </ToggleButton>
            <ToggleButton 
              value="Propose" 
              onClick={handleProposeButton} 
              selected={showProposeForm}
              sx={{ px: 2 }}
            >
              <Typography variant="body2" color={showProposeForm ? "primary" : "gray"}>
                Propose
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Forms - Appear directly below the toggle buttons with more spacing */}
        {showReserveForm && (
          <Box sx={{ mt: 1 }}>
            <ReserveParcelForm selectedFeatureOnMap={selectedFeatureOnMap} onClose={closeAllForms} />
          </Box>
        )}
        
        {showReleaseForm && (
          <Box sx={{ mt: 1 }}>
            <ReleaseParcelForm selectedFeatureOnMap={selectedFeatureOnMap} onClose={closeAllForms} />
          </Box>
        )}
        
        {showProposeForm && (
          <Box sx={{ mt: 2 }}>
            <ProposeParcelForm 
              selectedFeatureOnMap={selectedFeatureOnMap} 
              onFetchCenter={fetchParkCenter}
              onClose={closeAllForms}
            />
          </Box>
        )}
      </Box>

      {/* Map Container - Full screen */}
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        height: '100%',
        width: '100%'
      }}>
        {mapReady && defaultCenter ? (
          <ViewMap
            key={mapKey}
            setSelectedFeatureOnMap={setSelectedFeatureOnMap}
            isEditable={false}
            showLegend={true}
            showPopup={false}
            cent={finalCenter}
            highlightParcel={mapData}
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            bgcolor: '#f5f5f5'
          }}>
            <Typography>Loading Map...</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ParcelReservationRelease;